import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { fmtDuration } from "../lib/duration";
import {
    Anchor, Boat, CheckCircle, ClockCounterClockwise, Circle as CircleDashed, HourglassMedium,
    ChartPieSlice, Package, TrendUp, Wrench, ArrowsClockwise, Calendar,
} from "@phosphor-icons/react";

/**
 * Vessel Loading Overview — realistic bulk-carrier illustration with big
 * per-hatch cards (Completed / Loading / Pending), overhead cranes with
 * dashed connectors, and a 4-tile KPI strip below matching the customer
 * reference mock.
 */
export default function HatchOverlayCard({ vessel, metrics, canConfig, onOpenHatchChange, forReport }) {
    const [summary, setSummary] = useState(null);
    const [configOpen, setConfigOpen] = useState(false);
    const [loading, setLoading] = useState(true);
    const [lastUpdated, setLastUpdated] = useState(null);

    const load = async () => {
        try {
            setLoading(true);
            const { data } = await api.get(`/vessels/${vessel.id}/hatches`);
            setSummary(data);
            setLastUpdated(new Date());
        } catch {
            setSummary(null);
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [vessel.id, vessel.current_hatch, metrics?.latest_tob, metrics?.effective_tob]);

    const totalHatches = summary?.total_hatches || 0;

    if (!loading && totalHatches === 0) {
        return (
            <section className="g-card p-5" data-testid="hatch-overlay-card-unconfigured">
                <div className="flex items-start gap-3">
                    <Anchor size={22} weight="duotone" className="text-[var(--grindrod-navy)] shrink-0 mt-0.5" />
                    <div className="flex-1 min-w-0">
                        <div className="font-display font-black text-[var(--grindrod-navy)] text-lg">Hatch overlay not set up</div>
                        <div className="text-sm text-slate-500 mt-1">
                            Configure the number of hatches for <span className="font-semibold">{vessel.vessel_name}</span> to enable the loading overview.
                        </div>
                        {canConfig && (
                            <button onClick={() => setConfigOpen(true)} className="btn-primary mt-3 inline-flex items-center gap-2" data-testid="hatch-configure-btn">
                                <Wrench size={14} /> Configure hatches
                            </button>
                        )}
                    </div>
                </div>
                {configOpen && (
                    <HatchConfigModal vessel={vessel} onClose={() => setConfigOpen(false)} onSaved={() => { setConfigOpen(false); load(); }} />
                )}
            </section>
        );
    }

    const totalTob = Math.round(metrics?.latest_tob ?? metrics?.effective_tob ?? 0);
    const capacity = Math.round(vessel?.tonnage || 0);
    const totalLoadingMin = summary?.total_loading_time_minutes || 0;
    const pct = capacity > 0 ? Math.min(100, (totalTob / capacity) * 100) : 0;
    const avgRate = Number(metrics?.overall_rate ?? metrics?.avg_flow_rate ?? metrics?.average_loading_rate ?? 0);
    const productiveMin = Math.round(Number(metrics?.productive_minutes ?? 0));
    const unproductiveMin = Math.round(Number(metrics?.unproductive_minutes ?? 0));
    const berth = vessel?.berth || vessel?.division ? String(vessel?.berth || vessel?.division || "").toUpperCase() : null;
    const startDate = vessel?.start_datetime ? new Date(vessel.start_datetime) : null;

    return (
        <section className="ttv-vlo-card" data-testid="hatch-overlay-card">
            {/* Header */}
            <header className="ttv-vlo-header">
                <div className="flex items-start gap-3">
                    <div className="ttv-vlo-header-icon">
                        <Boat size={22} weight="duotone" />
                    </div>
                    <div>
                        <div className="font-display font-black text-2xl md:text-3xl text-[var(--grindrod-navy)] leading-tight">Vessel Loading Overview</div>
                        <div className="text-slate-500 text-sm">Real-time hatch loading status and performance</div>
                    </div>
                </div>
                <div className="ttv-vlo-vessel-card">
                    <Calendar size={18} weight="duotone" className="text-[#2563EB] shrink-0" />
                    <div className="text-[13px] leading-tight">
                        <div><span className="font-semibold">Vessel:</span> <span className="text-slate-700">{vessel?.vessel_name || "—"}</span></div>
                        {startDate && (
                            <div><span className="font-semibold text-[#2563EB]">Date:</span> <span className="text-slate-700">{startDate.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })}</span></div>
                        )}
                        {berth && (
                            <div><span className="font-semibold text-[#2563EB]">Berth:</span> <span className="text-slate-700">{berth}</span></div>
                        )}
                    </div>
                </div>
                {!forReport && (
                    <div className="ttv-vlo-actions">
                        {canConfig && (
                            <button
                                onClick={() => setConfigOpen(true)}
                                className="ttv-vlo-btn ttv-vlo-btn-outline"
                                data-testid="hatch-reconfigure-btn"
                            >
                                <Wrench size={13} /> Configure
                            </button>
                        )}
                        {onOpenHatchChange && (
                            <button
                                onClick={onOpenHatchChange}
                                className="ttv-vlo-btn ttv-vlo-btn-primary"
                                data-testid="hatch-overlay-change-btn"
                            >
                                <ArrowsClockwise size={13} weight="bold" /> Hatch change
                            </button>
                        )}
                    </div>
                )}
            </header>

            {/* Vessel + hatch overlay */}
            <VesselIllustration summary={summary} loading={loading} avgRate={Number(metrics?.overall_rate ?? metrics?.avg_flow_rate ?? metrics?.average_loading_rate ?? 0)} />

            {/* KPI strip */}
            <div className="ttv-vlo-kpi-strip" data-testid="vessel-kpi-strip">
                <KpiTile Icon={Package} label="Total Tons on Board" value={`${totalTob.toLocaleString()} t`} sub={`(Of ${capacity.toLocaleString()} t Capacity)`} accent="#2563EB" testid="kpi-total-tob" />
                <KpiTile Icon={ClockCounterClockwise} label="Productive Time" value={fmtDuration(productiveMin) || "00:00"} sub="(Loading was active)" accent="#10B981" testid="kpi-productive-time" />
                <KpiTile Icon={ClockCounterClockwise} label="Unproductive Time" value={fmtDuration(unproductiveMin) || "00:00"} sub="(Paused / downtime)" accent="#DC2626" testid="kpi-unproductive-time" />
                <KpiTile Icon={ChartPieSlice} label="Completion Progress" value={`${pct.toFixed(1)}%`} progress={pct} accent="#10B981" testid="kpi-progress" />
                <KpiTile Icon={TrendUp} label="Average Loading Rate" value={`${Math.round(avgRate).toLocaleString()} t/hr`} sub="(Since Commencement)" accent="#2563EB" testid="kpi-avg-rate" />
            </div>

            {/* Footer */}
            <footer className="ttv-vlo-footer">
                <div className="text-slate-500">All times are in hh:mm (hours:minutes) &nbsp;|&nbsp; All weights are in metric tons (t)</div>
                {lastUpdated && !forReport && (
                    <div className="text-slate-500"><ArrowsClockwise size={11} weight="bold" className="inline text-[#2563EB]" /> Last Updated: <span className="font-semibold text-slate-700">{lastUpdated.toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })}</span></div>
                )}
            </footer>

            {configOpen && (
                <HatchConfigModal vessel={vessel} initial={summary} onClose={() => setConfigOpen(false)} onSaved={() => { setConfigOpen(false); load(); }} />
            )}
        </section>
    );
}

/* ============================================================
   Vessel illustration + hatch cards
   ============================================================ */
function VesselIllustration({ summary, loading, avgRate = 0 }) {
    const hatches = summary?.hatches || [];
    const N = Math.max(1, hatches.length);
    // Ship geometry inside a 1400×360 viewBox. Deck area (where hatch labels
    // + cards line up) spans from x=210 (stern block ends) to x=1200 (bow
    // starts tapering) — see the SVG hull path below.
    const DECK_X_START = 210;
    const DECK_X_END = 1200;
    const VB_W = 1400;
    const activeHatch = hatches.find((h) => h.status === "in_progress");
    const activeIdx = hatches.findIndex((h) => h.status === "in_progress");

    // Compute where the hatch cards sit as % of the illustration width so
    // they anchor to the same X positions as the hatch coamings drawn in
    // the SVG below.
    const deckLeftPct = (DECK_X_START / VB_W) * 100;
    const deckRightPct = ((VB_W - DECK_X_END) / VB_W) * 100;

    return (
        <div className="ttv-vlo-illustration" data-testid="vessel-silhouette">
            {/* Ship + hatch labels share the same scrollable wrap so they stay
                aligned on narrow screens (labels used to compress while the
                ship scrolled at 900px min-width, causing text to cross the hull). */}
            <div className="ttv-vlo-ship-wrap">
                {/* HATCH labels row above the ship */}
                <div className="ttv-vlo-labels" style={{ paddingLeft: `${deckLeftPct}%`, paddingRight: `${deckRightPct}%` }}>
                    <div className="ttv-vlo-labels-grid" style={{ gridTemplateColumns: `repeat(${N}, 1fr)` }}>
                        {hatches.map((h) => (
                            <div key={`label-${h.hatch_no}`} className="ttv-vlo-label">
                                <div className="ttv-vlo-label-text">HATCH {h.hatch_no}</div>
                                <div className="ttv-vlo-dash" />
                            </div>
                        ))}
                    </div>
                </div>

                {/* Continuous vessel SVG — one ship outline, cranes + hatch coamings drawn in place */}
                <svg viewBox={`0 0 ${VB_W} 430`} className="ttv-vlo-ship-svg" preserveAspectRatio="xMidYMid meet" aria-hidden>
                    <defs>
                        <linearGradient id="hull-navy" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0" stopColor="#1B2C48" />
                            <stop offset="1" stopColor="#0A1830" />
                        </linearGradient>
                        <linearGradient id="hull-red" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0" stopColor="#C0272F" />
                            <stop offset="1" stopColor="#7C1518" />
                        </linearGradient>
                        <linearGradient id="water-fill" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0" stopColor="#93C5FD" />
                            <stop offset="1" stopColor="#3B82F6" />
                        </linearGradient>
                    </defs>

                    {/* Water */}
                    <rect x="0" y="360" width={VB_W} height="70" fill="url(#water-fill)" />
                    <g stroke="#DBEAFE" strokeWidth="1.5" fill="none" opacity="0.85">
                        <path d="M0 380 Q 200 372 400 380 T 800 380 T 1200 380 T 1400 380" />
                        <path d="M0 400 Q 200 392 400 400 T 800 400 T 1200 400 T 1400 400" />
                        <path d="M0 418 Q 200 410 400 418 T 800 418 T 1200 418 T 1400 418" />
                    </g>

                    {/* ============== HULL (navy top + red bottom) ============== */}
                    {/* Top navy hull (above waterline) */}
                    <path
                        d="M 90 260
                           Q 70 260 65 300
                           L 65 350
                           L 1220 350
                           Q 1320 350 1370 300
                           Q 1385 275 1355 262
                           L 90 260 Z"
                        fill="url(#hull-navy)" stroke="#050C1C" strokeWidth="1.5"
                    />
                    {/* Red boot-topping band (below waterline visible slice) */}
                    <path
                        d="M 65 350
                           L 1220 350
                           Q 1320 350 1370 300
                           Q 1355 380 1220 388
                           L 65 388 Z"
                        fill="url(#hull-red)" stroke="#590F12" strokeWidth="1"
                    />
                    {/* White waterline stripe */}
                    <path d="M 65 352 L 1220 352 Q 1320 352 1370 302" stroke="#F1F5FA" strokeWidth="2.5" fill="none" opacity="0.9" />

                    {/* Deck plate (thin band on top of hull) */}
                    <rect x="90" y="252" width="1180" height="12" fill="#182238" stroke="#050C1C" strokeWidth="0.5" />

                    {/* ============== Stern superstructure (left) — tall, multi-story ============== */}
                    <g>
                        {/* Base tower block */}
                        <rect x="105" y="90" width="140" height="162" fill="#F1F5F9" stroke="#334155" strokeWidth="1.2" />
                        {/* Bridge (top narrower section) */}
                        <rect x="118" y="60" width="114" height="34" fill="#F8FAFC" stroke="#334155" strokeWidth="1" />
                        {/* Bridge windows (dark strip) */}
                        <rect x="122" y="66" width="106" height="14" fill="#0F172A" />
                        {/* Rows of window strips on lower block */}
                        {[102, 122, 142, 162, 182, 202, 222].map((y) => (
                            <g key={y}>
                                <rect x="110" y={y} width="20" height="10" fill="#0F172A" />
                                <rect x="135" y={y} width="20" height="10" fill="#0F172A" />
                                <rect x="160" y={y} width="20" height="10" fill="#0F172A" />
                                <rect x="185" y={y} width="20" height="10" fill="#0F172A" />
                                <rect x="210" y={y} width="20" height="10" fill="#0F172A" />
                            </g>
                        ))}
                        {/* Radar mast */}
                        <line x1="175" y1="20" x2="175" y2="60" stroke="#334155" strokeWidth="2.5" />
                        <line x1="150" y1="34" x2="200" y2="34" stroke="#334155" strokeWidth="2" />
                        <circle cx="175" cy="20" r="4" fill="#334155" />
                        {/* Funnel behind superstructure */}
                        <rect x="82" y="150" width="24" height="100" fill="#E2E8F0" stroke="#334155" strokeWidth="1.2" />
                        <rect x="82" y="150" width="24" height="20" fill="#DC2626" />
                    </g>

                    {/* ============== Bow (right) — pointed with anchor ============== */}
                    <g>
                        {/* Anchor windlass on deck */}
                        <rect x="1230" y="235" width="30" height="18" fill="#94A3B8" stroke="#334155" strokeWidth="0.8" />
                        {/* Anchor icon on hull side */}
                        <g transform="translate(1300, 305)">
                            <circle r="12" fill="#F1F5F9" stroke="#334155" strokeWidth="1" />
                            <path d="M 0 -6 L 0 6 M -5 4 A 5 5 0 0 0 5 4 M -6 -6 L 6 -6" stroke="#334155" strokeWidth="1.5" fill="none" strokeLinecap="round" />
                        </g>
                    </g>

                    {/* ============== Hatch coamings + gantry-style cranes ============== */}
                    <g>
                        {hatches.map((h, i) => {
                            const usableW = DECK_X_END - DECK_X_START;
                            const gap = 8;
                            const boxW = (usableW - gap * (N - 1)) / N;
                            const x = DECK_X_START + i * (boxW + gap);
                            const isActive = h.status === "in_progress";
                            const cx = x + boxW / 2;
                            return (
                                <g key={`hatch-${h.hatch_no}`}>
                                    {/* Tall gantry crane behind each hatch */}
                                    <g>
                                        {/* Vertical mast (2 legs) */}
                                        <rect x={cx - 22} y={95} width="6" height={155} fill="#94A3B8" />
                                        <rect x={cx + 16} y={95} width="6" height={155} fill="#94A3B8" />
                                        {/* Cross beam (top) */}
                                        <rect x={cx - 30} y={90} width={60} height={10} fill="#64748B" />
                                        {/* Cabin */}
                                        <rect x={cx - 14} y={98} width={28} height={16} fill="#475569" />
                                        {/* Jib arm (diagonal) */}
                                        <line x1={cx} y1={100} x2={cx + 60} y2={140} stroke="#64748B" strokeWidth="5" strokeLinecap="round" />
                                        <line x1={cx} y1={100} x2={cx + 60} y2={140} stroke="#94A3B8" strokeWidth="1.5" />
                                        <circle cx={cx + 60} cy={140} r={4} fill="#334155" />
                                        {/* Hoist cable */}
                                        <line x1={cx + 60} y1={143} x2={cx + 60} y2={200} stroke="#0F172A" strokeWidth="1" />
                                    </g>
                                    {/* Hatch coaming (raised block on deck) */}
                                    <rect
                                        x={x} y={230} width={boxW} height={24}
                                        fill="#1E293B" stroke={isActive ? "#3B82F6" : "#050C1C"} strokeWidth={isActive ? 2 : 1}
                                        rx="1"
                                    />
                                    {/* Segmented cover lines */}
                                    {Array.from({ length: 3 }).map((_, j) => {
                                        const rx = x + (boxW / 3) * (j + 1);
                                        return <line key={j} x1={rx} y1={232} x2={rx} y2={252} stroke="#4A5468" strokeWidth="0.8" opacity="0.7" />;
                                    })}
                                    {isActive && (
                                        <rect x={x} y={230} width={boxW} height={24} fill="#3B82F6" opacity="0.3">
                                            <animate attributeName="opacity" values="0.15;0.45;0.15" dur="1.6s" repeatCount="indefinite" />
                                        </rect>
                                    )}
                                </g>
                            );
                        })}
                    </g>
                </svg>

                {/* Info cards — ABSOLUTELY positioned OVER the vessel's deck area (like the reference mock) */}
                <div className="ttv-vlo-info-overlay" style={{ left: `${deckLeftPct}%`, right: `${deckRightPct}%` }}>
                    <div className="ttv-vlo-info-grid" style={{ gridTemplateColumns: `repeat(${N}, 1fr)` }}>
                        {loading ? (
                            <div className="col-span-full text-slate-500 text-xs uppercase-label text-center py-6 animate-pulse">
                                Loading hatches…
                            </div>
                        ) : (
                            hatches.map((h) => <HatchInfo key={h.hatch_no} h={h} avgRate={avgRate} />)
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

function computePace({ status, target, loaded, durationMin, avgRate }) {
    // Returns { color, label } based on hatch pace vs vessel avg. Rules:
    //   pending    → neutral grey (no verdict yet)
    //   completed  → green (delivered)
    //   in_progress:
    //     ahead / on pace           → green
    //     ≤ 30 min behind target    → amber
    //     > 30 min behind target    → red
    if (status === "pending") return { color: "#64748B", label: "Pending", pill: "#F1F5F9", pillText: "#475569" };
    if (status === "completed") return { color: "#10B981", label: "Done", pill: "#D1FAE5", pillText: "#047857" };
    // in_progress
    if (!(target > 0) || !(avgRate > 0) || !(durationMin > 0)) {
        return { color: "#2563EB", label: "Loading", pill: "#DBEAFE", pillText: "#1D4ED8" };
    }
    const hatchPaceTph = loaded / (durationMin / 60);        // t/hr for this hatch so far
    const remaining = Math.max(0, target - loaded);
    if (remaining === 0) return { color: "#10B981", label: "On target", pill: "#D1FAE5", pillText: "#047857" };
    const remainingExpectedHrs = remaining / avgRate;         // at vessel avg pace
    const remainingActualHrs = hatchPaceTph > 0 ? remaining / hatchPaceTph : Infinity;
    const behindMin = (remainingActualHrs - remainingExpectedHrs) * 60;
    if (behindMin <= 0) return { color: "#10B981", label: "On pace", pill: "#D1FAE5", pillText: "#047857" };
    if (behindMin <= 30) return { color: "#F59E0B", label: `+${Math.round(behindMin)}m slip`, pill: "#FEF3C7", pillText: "#B45309" };
    return { color: "#DC2626", label: `+${Math.round(behindMin)}m late`, pill: "#FEE2E2", pillText: "#B91C1C" };
}

function HatchInfo({ h, avgRate = 0 }) {
    const target = Number(h.target_tons || 0);
    const loaded = Number(h.tons_loaded || 0);
    const durationMin = Number(h.duration_min || 0);
    const pct = target > 0 ? Math.min(100, (loaded / target) * 100) : 0;
    const pace = computePace({ status: h.status, target, loaded, durationMin, avgRate });
    const isInProgress = h.status === "in_progress";
    const isCompleted = h.status === "completed";

    return (
        <div className="ttv-vlo-info" style={{ borderColor: pace.color, boxShadow: `0 8px 18px -10px ${pace.color}55` }} data-testid={`hatch-box-${h.hatch_no}`}>
            <div className="ttv-vlo-tons" style={{ background: pace.color }} data-testid={`hatch-tons-${h.hatch_no}`}>
                {Math.round(loaded).toLocaleString()} <span className="ttv-vlo-tons-unit">t</span>
            </div>
            {target > 0 && (
                <div className="ttv-vlo-target" data-testid={`hatch-target-display-${h.hatch_no}`}>
                    <div className="ttv-vlo-target-label">
                        <span className="ttv-vlo-target-tag">Tgt</span>
                        <span className="ttv-vlo-target-num tabular">{Math.round(target).toLocaleString()}<span className="ttv-vlo-target-unit"> t</span></span>
                    </div>
                    <div className="ttv-vlo-target-bar">
                        <div className="ttv-vlo-target-bar-fill" style={{ width: `${pct}%`, background: pace.color }} />
                        <span className="ttv-vlo-target-bar-pct tabular" style={{ color: pct >= 90 ? "#FFFFFF" : "#0F172A" }}>{pct.toFixed(0)}%</span>
                    </div>
                </div>
            )}
            <div
                className="ttv-vlo-pace-pill"
                style={{ background: pace.pill, color: pace.pillText }}
                data-testid={`hatch-pace-${h.hatch_no}`}
                title={pace.label}
            >
                {pace.label}
            </div>
            <div className="ttv-vlo-status-icon">
                {isCompleted && <CheckCircle size={26} weight="fill" color={pace.color} />}
                {isInProgress && (
                    <span className="ttv-vlo-loading-ring"><CircleDashed size={24} color={pace.color} /></span>
                )}
                {!isCompleted && !isInProgress && <HourglassMedium size={24} weight="duotone" color="#94A3B8" />}
            </div>
            <div className="ttv-vlo-time">
                <ClockCounterClockwise size={11} weight="bold" className="text-slate-500" />
                <span className="tabular text-slate-700 font-semibold">{durationMin > 0 ? (fmtDuration(durationMin) || "00:00") : "—"}</span>
            </div>
        </div>
    );
}

/* ============================================================
   KPI tile
   ============================================================ */
function KpiTile({ Icon, label, value, sub, progress, accent, testid }) {
    return (
        <div className="ttv-vlo-kpi" data-testid={testid}>
            <div className="ttv-vlo-kpi-icon" style={{ background: `${accent}18`, color: accent }}>
                <Icon size={22} weight="duotone" />
            </div>
            <div className="flex-1 min-w-0">
                <div className="ttv-vlo-kpi-label">{label}</div>
                <div className="ttv-vlo-kpi-value" style={progress != null ? { color: accent } : undefined}>{value}</div>
                {sub && <div className="ttv-vlo-kpi-sub">{sub}</div>}
                {progress != null && (
                    <div className="ttv-vlo-kpi-bar">
                        <div className="ttv-vlo-kpi-bar-fill" style={{ width: `${Math.max(0, Math.min(100, progress))}%`, background: accent }} />
                    </div>
                )}
            </div>
        </div>
    );
}

/* ============================================================
   Config modal
   ============================================================ */
function HatchConfigModal({ vessel, initial, onClose, onSaved }) {
    const initN = initial?.total_hatches || vessel?.total_hatches || 5;
    const initTargets = useMemo(() => {
        const src = initial?.hatches || [];
        const m = {};
        src.forEach((h) => {
            if (h?.target_tons > 0) m[String(h.hatch_no)] = String(h.target_tons);
        });
        return m;
    }, [initial]);
    const [totalHatches, setTotalHatches] = useState(String(initN));
    const [targets, setTargets] = useState(initTargets);
    const [saving, setSaving] = useState(false);

    const setTarget = (n, v) => setTargets((t) => ({ ...t, [String(n)]: v }));

    const submit = async () => {
        const n = parseInt(totalHatches);
        if (!n || n < 1 || n > 20) return toast.error("Enter a hatch count between 1 and 20");
        try {
            setSaving(true);
            const parsedTargets = {};
            Object.entries(targets).forEach(([k, v]) => {
                const num = parseFloat(v);
                if (num > 0) parsedTargets[k] = num;
            });
            await api.post(`/vessels/${vessel.id}/hatches/config`, { total_hatches: n, hatch_targets: parsedTargets });
            toast.success(`Configured ${n} hatches`);
            onSaved?.();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed to save hatch config");
        } finally {
            setSaving(false);
        }
    };

    const n = Math.max(1, Math.min(20, parseInt(totalHatches) || 0));

    return (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-end sm:items-center justify-center p-2 sm:p-4" data-testid="hatch-config-modal">
            <div className="bg-white w-full max-w-lg rounded-sm shadow-2xl border border-slate-200 flex flex-col max-h-[90vh]">
                <div className="p-4 border-b border-slate-200 flex items-center gap-3">
                    <Wrench size={20} weight="fill" className="text-[var(--grindrod-navy)]" />
                    <div className="flex-1">
                        <div className="font-display font-black uppercase tracking-wide">Configure hatches — {vessel.vessel_name}</div>
                        <div className="text-xs text-slate-500">Set the total number of hatches and (optionally) a target tonnage for each one.</div>
                    </div>
                    <button onClick={onClose} className="text-slate-400 hover:text-slate-700 text-sm" data-testid="hatch-config-close">✕</button>
                </div>
                <div className="p-4 space-y-4 overflow-y-auto">
                    <div>
                        <label className="uppercase-label block mb-1.5">Total hatches *</label>
                        <input
                            type="number"
                            min="1"
                            max="20"
                            className="g-input tabular w-32"
                            value={totalHatches}
                            onChange={(e) => setTotalHatches(e.target.value)}
                            data-testid="hatch-config-count"
                        />
                    </div>
                    <div>
                        <div className="uppercase-label mb-2">Per-hatch target (optional)</div>
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                            {Array.from({ length: n }, (_, i) => i + 1).map((hn) => (
                                <div key={hn} className="flex items-center gap-2">
                                    <span className="font-display font-black text-sm w-8 text-[var(--grindrod-navy)]">H{hn}</span>
                                    <input
                                        type="number"
                                        min="0"
                                        step="50"
                                        placeholder="Target t"
                                        className="g-input tabular w-full"
                                        value={targets[String(hn)] || ""}
                                        onChange={(e) => setTarget(hn, e.target.value)}
                                        data-testid={`hatch-target-${hn}`}
                                    />
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
                <div className="p-4 border-t border-slate-200 flex justify-end gap-2">
                    <button onClick={onClose} className="btn-outline" data-testid="hatch-config-cancel">Cancel</button>
                    <button onClick={submit} disabled={saving} className="btn-primary" data-testid="hatch-config-save">
                        {saving ? "Saving…" : "Save configuration"}
                    </button>
                </div>
            </div>
        </div>
    );
}
